import React, { ReactNode, useCallback, useMemo, useState } from "react";
import { Empty, Popover, Skeleton, Table, Tag, Tooltip } from "antd";
import { CaretDownOutlined, CaretRightOutlined, RightOutlined } from "@ant-design/icons";
import { DaySheet, DaySheetActivity, DaySheetPhase, DaySheetTask } from "../../../../api/SiteDaily";
import { useFontScale } from "../../../../theme/fontScale";
import { GenericStatusPill, StatusTone } from "../../shared/statusColors";
import { useWbsTreeName } from "../../shared/useWbsCodes";
import { splitWbsCode } from "../../shared/wbsCode";
import { fmtQty } from "../executionNorm";
import { fmtPerUnit, perUnitLabel, perUnitOf } from "./daySheet";

// The day sheet is a TREE: phase -> activity -> task, and a task opens into the
// section's editor (material lines, or machines). Shared by Material-Stores and
// Mechanical; the two differ only in what an opened task shows.
//
// Built as a FLAT dataSource with our own carets rather than antd's nested
// `children`: antd cannot combine tree children with `expandedRowRender` on the
// same table, and the editor under a task IS an expanded row. Group open/closed
// is ours; the task editor's open/closed is the caller's (controlled), because
// the caller owns the drafts that live inside it.

export type SheetRowLevel = "phase" | "activity" | "task";

export interface TaskRowState {
  dirty?: boolean;
  invalid?: boolean;
  saving?: boolean;
  // The task's cost for the day across its loaded lines. Absent until the task
  // has been opened (its lines are fetched lazily) — unless the caller can
  // supply the day's figures for every task from a rollup, as Material-Stores
  // does, in which case it is resolved there (live draft wins over the rollup).
  amount?: number | null;
  // The day's TARGET for this task (norm x produced, at the norm's rate), and
  // the per-category split behind the figure. Both optional: the Mechanical
  // sheet passes neither and keeps its previous behaviour exactly.
  targetAmount?: number | null;
  categories?: Array<{ name: string; target: string; actual: string }>;
}

interface SheetRow {
  key: string;
  level: SheetRowLevel;
  phase?: DaySheetPhase;
  activity?: DaySheetActivity;
  task?: DaySheetTask;
  taskCount: number;
  recordedTaskCount: number;
}

interface Props {
  sheet: DaySheet | null;
  loading: boolean;
  expandedTaskIds: number[];
  onExpandedTaskIdsChange: (ids: number[]) => void;
  renderTaskDetail: (task: DaySheetTask) => ReactNode;
  // When given, a task opens ELSEWHERE (Material-Stores' drawer) instead of
  // expanding inline; activeTaskId marks the one currently open there.
  onTaskOpen?: (task: DaySheetTask) => void;
  activeTaskId?: number | null;
  taskState?: (task: DaySheetTask) => TaskRowState | undefined;
  emptyDescription: ReactNode;
  // A tracked chip on tracked tasks — meaningful only when the sheet shows
  // ALL tasks of a project that has tracking configured.
  showTrackedTag: boolean;
  // Civil-done sheets: how much is still owed on a task for THIS section, and
  // what to call it. Shows a tag and switches the produced column to
  // production till date (no single day is being entered against).
  pendingCountOf?: (task: DaySheetTask) => number;
  pendingLabel?: string;
}

const RECORDED: StatusTone = { dot: "#389e0d", text: "#237804", bg: "#f6ffed" };
const PENDING: StatusTone = { dot: "#8094ae", text: "#526484", bg: "#f0f2f5" };
const UNSAVED: StatusTone = { dot: "#fa8c16", text: "#d46b08", bg: "#fff7e6" };
const INVALID: StatusTone = { dot: "#f5222d", text: "#cf1322", bg: "#fff1f0" };

const taskKey = (taskId: number) => `t-${taskId}`;

const DaySheetTree: React.FC<Props> = ({
  sheet,
  loading,
  expandedTaskIds,
  onExpandedTaskIdsChange,
  renderTaskDetail,
  onTaskOpen,
  activeTaskId,
  taskState,
  emptyDescription,
  showTrackedTag,
  pendingCountOf,
  pendingLabel = "material",
}) => {
  const { scale } = useFontScale();
  const w = (base: number) => Math.round(base * scale);

  // Groups start OPEN — the sheet is read top-down as "what needs recording
  // today" — so the state holds what is CLOSED and new data arrives open.
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  const toggleGroup = useCallback((key: string) => {
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const toggleTask = useCallback(
    (taskId: number) => {
      onExpandedTaskIdsChange(
        expandedTaskIds.includes(taskId)
          ? expandedTaskIds.filter((id) => id !== taskId)
          : [...expandedTaskIds, taskId]
      );
    },
    [expandedTaskIds, onExpandedTaskIdsChange]
  );

  const activityNodes = useMemo(
    () =>
      (sheet?.phases || []).flatMap((phase) =>
        phase.activities.map((activity) => ({
          key: `a:${activity.activityId}`,
          name: activity.activityName,
        }))
      ),
    [sheet]
  );
  const activityLabel = useWbsTreeName(activityNodes);

  const rows = useMemo<SheetRow[]>(() => {
    const out: SheetRow[] = [];
    (sheet?.phases || []).forEach((phase) => {
      const phaseKey = `p-${phase.phaseId}`;
      out.push({
        key: phaseKey,
        level: "phase",
        phase,
        taskCount: phase.taskCount,
        recordedTaskCount: phase.recordedTaskCount,
      });
      if (collapsed.has(phaseKey)) return;
      phase.activities.forEach((activity) => {
        const activityKey = `${phaseKey}-a-${activity.activityId}`;
        out.push({
          key: activityKey,
          level: "activity",
          activity,
          taskCount: activity.taskCount,
          recordedTaskCount: activity.recordedTaskCount,
        });
        if (collapsed.has(activityKey)) return;
        activity.tasks.forEach((task) =>
          out.push({
            key: taskKey(task.taskId),
            level: "task",
            task,
            taskCount: 1,
            recordedTaskCount: task.recordedCount > 0 ? 1 : 0,
          })
        );
      });
    });
    return out;
  }, [sheet, collapsed]);

  const expandedRowKeys = useMemo(() => expandedTaskIds.map(taskKey), [expandedTaskIds]);

  const caret = (open: boolean, onClick: () => void) => (
    <button
      type="button"
      className="pms-exec-caret"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      aria-label={open ? "Collapse" : "Expand"}
    >
      {open ? <CaretDownOutlined /> : <CaretRightOutlined />}
    </button>
  );

  const groupCount = (row: SheetRow) => (
    <span className="pms-exec-group-count">
      {row.recordedTaskCount} of {row.taskCount} recorded
    </span>
  );

  const widths = {
    name: w(400),
    uom: w(90),
    produced: w(160),
    cost: w(200),
    status: w(140),
  };
  const scrollX = Object.values(widths).reduce((acc, n) => acc + n, 0);

  const columns = [
    {
      title: "Phase / Activity / Task",
      key: "name",
      width: widths.name,
      fixed: "left" as const,
      render: (_: any, row: SheetRow) => {
        if (row.level === "phase") {
          const { code, label } = splitWbsCode(row.phase?.phaseName || "");
          return (
            <span style={{ color: "#022c57" }}>
              {caret(!collapsed.has(row.key), () => toggleGroup(row.key))}
              {row.phase?.phaseId ? label || `Phase ${row.phase.phaseId}` : "No phase"}
              {code && (
                <Tag style={{ marginInlineStart: 6 }} color="default">
                  {code}
                </Tag>
              )}
              {groupCount(row)}
            </span>
          );
        }
        if (row.level === "activity") {
          return (
            <span style={{ color: "#364a63", paddingLeft: 18 }}>
              {caret(!collapsed.has(row.key), () => toggleGroup(row.key))}
              {activityLabel(`a:${row.activity?.activityId}`, row.activity?.activityName)}
              {groupCount(row)}
            </span>
          );
        }
        const task = row.task as DaySheetTask;
        const open = expandedTaskIds.includes(task.taskId);
        return (
          <span style={{ paddingLeft: 36, display: "inline-flex", alignItems: "center", minWidth: 0 }}>
            {onTaskOpen ? (
              <span className="pms-exec-caret" aria-hidden>
                <RightOutlined style={{ fontSize: 10 }} />
              </span>
            ) : (
              caret(open, () => toggleTask(task.taskId))
            )}
            <span className="pms-exec-task-seq">{task.taskSequenceId}</span>
            <Tooltip title={task.taskName.length > 40 ? task.taskName : ""}>
              <span
                className="pms-exec-task-name"
                style={{
                  maxWidth: widths.name - 200,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  display: "inline-block",
                  verticalAlign: "bottom",
                }}
              >
                {task.taskName}
              </span>
            </Tooltip>
            {showTrackedTag && task.isMonitored && (
              <Tooltip title="Tracked on this date">
                <span className="pms-exec-count-badge">tracked</span>
              </Tooltip>
            )}
            {(pendingCountOf ? pendingCountOf(task) : task.pendingMaterialCount) > 0 && (
              <Tooltip
                title={`${pendingCountOf ? pendingCountOf(task) : task.pendingMaterialCount} line(s) of ${pendingLabel} still owed to civil work already done — book them here and they are counted against that work`}
              >
                <Tag style={{ marginInlineStart: 6 }} color="orange">
                  pending {pendingLabel}
                </Tag>
              </Tooltip>
            )}
          </span>
        );
      },
    },
    {
      title: "Work UOM",
      key: "uom",
      width: widths.uom,
      render: (_: any, row: SheetRow) =>
        row.level === "task" ? <span style={{ color: "#526484" }}>{row.task?.workUomName || "—"}</span> : null,
    },
    {
      title: pendingCountOf ? "Civil done (till date)" : "Produced today",
      key: "produced",
      width: widths.produced,
      align: "right" as const,
      render: (_: any, row: SheetRow) => {
        if (row.level !== "task") return null;
        const task = row.task as DaySheetTask;
        const produced = pendingCountOf ? task.producedTillDate : task.producedQty;
        if (produced === "") return <span style={{ color: "#8094ae" }}>—</span>;
        return (
          <span className="tabular-nums" style={{ color: "#022c57", fontWeight: 600 }}>
            {fmtQty(produced)}{" "}
            <span style={{ fontWeight: 400, color: "#8094ae" }}>{task.workUomName}</span>
          </span>
        );
      },
    },
    {
      // Task level: the day's cost and that per unit of what Civil produced.
      title: "Cost today",
      key: "cost",
      width: widths.cost,
      align: "right" as const,
      render: (_: any, row: SheetRow) => {
        if (row.level !== "task") return null;
        const task = row.task as DaySheetTask;
        const state = taskState?.(task);
        const amount = state?.amount;
        const target = state?.targetAmount ?? null;
        const categories = state?.categories;
        // Nothing recorded AND nothing planned: the honest blank. The "open the
        // task" hint survives only where the caller supplies no day figures at
        // all (the Mechanical sheet).
        if ((amount === undefined || amount === null || amount <= 0) && target === null) {
          return (
            <Tooltip title={amount === undefined ? "Open the task to see its cost" : ""}>
              <span style={{ color: "#8094ae" }}>—</span>
            </Tooltip>
          );
        }
        const shown = amount === undefined || amount === null || amount <= 0 ? null : amount;
        const perUnit = shown === null ? null : perUnitOf(shown, task.producedQty);
        const unit = perUnitLabel(task.workUomName);
        const figure = (
          <span
            className="tabular-nums"
            style={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-end", lineHeight: 1.3 }}
          >
            <span style={{ color: shown === null ? "#8094ae" : "#022c57", fontWeight: 600 }}>
              {shown === null ? "—" : `₹ ${fmtPerUnit(shown)}`}
            </span>
            {perUnit !== null && (
              <span style={{ fontSize: 11, color: "#8094ae" }}>
                ₹ {fmtPerUnit(perUnit)} / {unit}
              </span>
            )}
            {target !== null && (
              <span style={{ fontSize: 11, color: "#8094ae" }}>target ₹ {fmtPerUnit(target)}</span>
            )}
          </span>
        );
        if (!categories?.length) return figure;
        // The per-category split, so a closed task still answers "on what?".
        return (
          <Popover
            placement="left"
            title={`${task.taskSequenceId} — materials today`}
            content={
              <div style={{ minWidth: 260 }}>
                {categories.map((category) => (
                  <div
                    key={category.name}
                    style={{ display: "flex", justifyContent: "space-between", gap: 16, padding: "2px 0" }}
                  >
                    <span style={{ color: "#526484" }}>{category.name}</span>
                    <span className="tabular-nums">
                      <span style={{ color: "#8094ae" }}>{category.target === "" ? "—" : `₹ ${fmtQty(category.target)}`}</span>
                      <span style={{ color: "#8094ae" }}> → </span>
                      <span style={{ color: "#022c57", fontWeight: 600 }}>
                        {category.actual === "" ? "—" : `₹ ${fmtQty(category.actual)}`}
                      </span>
                    </span>
                  </div>
                ))}
                <div style={{ marginTop: 6, fontSize: 11, color: "#8094ae" }}>target → actual, per category</div>
              </div>
            }
          >
            {figure}
          </Popover>
        );
      },
    },
    {
      title: "Status",
      key: "status",
      width: widths.status,
      render: (_: any, row: SheetRow) => {
        if (row.level !== "task") return null;
        const task = row.task as DaySheetTask;
        const state = taskState?.(task);
        if (state?.invalid) return <GenericStatusPill label="Fix errors" tone={INVALID} />;
        if (state?.saving) return <GenericStatusPill label="Saving…" tone={UNSAVED} />;
        if (state?.dirty) return <GenericStatusPill label="Unsaved" tone={UNSAVED} />;
        return task.recordedCount > 0 ? (
          <GenericStatusPill label="Recorded" tone={RECORDED} />
        ) : (
          <GenericStatusPill label="Pending" tone={PENDING} />
        );
      },
    },
  ];

  if (loading && !sheet) {
    return (
      <div className="pms-exec-skeleton-card">
        <Skeleton active title={false} paragraph={{ rows: 7, width: ["40%", "60%", "90%", "90%", "60%", "90%", "90%"] }} />
      </div>
    );
  }

  return (
    <Table
      size="small"
      rowKey="key"
      className="pms-exec-sheet"
      loading={loading}
      dataSource={rows}
      columns={columns as any}
      pagination={false}
      scroll={{ x: scrollX }}
      expandable={{
        showExpandColumn: false,
        expandedRowKeys: onTaskOpen ? [] : expandedRowKeys,
        rowExpandable: (row: SheetRow) => row.level === "task",
        expandedRowRender: (row: SheetRow) => (row.task ? renderTaskDetail(row.task) : null),
      }}
      rowClassName={(row: SheetRow) =>
        [
          `pms-exec-row-${row.level}`,
          "pms-exec-row-clickable",
          row.level === "task" &&
          row.task &&
          (onTaskOpen ? activeTaskId === row.task.taskId : expandedTaskIds.includes(row.task.taskId))
            ? "pms-exec-row-open"
            : "",
        ]
          .filter(Boolean)
          .join(" ")
      }
      onRow={(row: SheetRow) => ({
        onClick: () => {
          if (row.level === "task" && row.task) {
            if (onTaskOpen) onTaskOpen(row.task);
            else toggleTask(row.task.taskId);
          }
          else toggleGroup(row.key);
        },
      })}
      locale={{
        emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyDescription} />,
      }}
    />
  );
};

export default DaySheetTree;
